import React from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Card,
  CardContent,
  Link
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import DemographicsChart from './charts/DemographicsChart';
import { SectionRenderer } from './FormattedNarrative';
import DebugPanel from './DebugPanel';
import DataSourcesInfo from './DataSourcesInfo';

const CompetitiveSummaryTab = ({ summary, loading, onGenerate, onRegenerate, schoolName, selectedSchool, showAdminControls = true, showDataSources = true }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Generating competitive summary...</Typography>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No competitive summary generated yet
        </Typography>
        <Button
          variant="contained"
          onClick={onGenerate}
          startIcon={<RefreshIcon />}
          sx={{ mt: 2 }}
        >
          Generate Competitive Summary
        </Button>
      </Box>
    );
  }

  if (summary.error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={onGenerate}>
          Retry
        </Button>
      }>
        {summary.error}
      </Alert>
    );
  }

  // Extract fields from summary (works for both live and stored+data formats)
  const { data, narrative, sources, from_cache, tokens_used, mode } = summary;

  // Try to get structured sections from debug.parsed_sections (preferred for table rendering)
  // Fall back to parsing markdown if not available
  const getProcessedSections = () => {
    // Check for structured parsed_sections from backend (includes table data)
    // Try both debug.parsed_sections (from live generation) and sections (from database)
    // Note: summary.sections from DB is already the full parsed_sections object {sections: [...], executive_summary: "..."}
    const parsedSections = summary.debug?.parsed_sections || summary.sections;
    
    if (parsedSections && parsedSections.sections && parsedSections.sections.length > 0) {
      // Use structured sections - they include table data
      const sections = [];
      let demographicPositioning = null;
      let executiveSummary = parsedSections.executive_summary;
      
      parsedSections.sections.forEach(section => {
        if (section.title === 'Demographic Positioning') {
          demographicPositioning = section;
        } else if (section.title !== 'Market Share Analysis') {
          sections.push(section); // Includes type: 'table' and competitors for table sections
        }
      });
      
      return { sections, demographicPositioning, executiveSummary };
    }
    
    // Fallback: Parse markdown narrative
    if (!narrative) return { sections: [], demographicPositioning: null, executiveSummary: null };
    
    const sections = [];
    let demographicPositioning = null;
    let executiveSummary = null;
    
    // Split by ## headers (main sections)
    const parts = narrative.split(/^## /gm).filter(Boolean);
    
    parts.forEach(part => {
      // Get section title and content
      const lines = part.split('\n');
      const title = lines[0].trim();
      const content = lines.slice(1).join('\n').trim();
      
      if (title === 'Executive Summary') {
        executiveSummary = content;
      } else if (title === 'Demographic Positioning') {
        demographicPositioning = { title, content };
      } else if (title !== 'Market Share Analysis') {
        sections.push({ title, content });
      }
    });
    
    return { sections, demographicPositioning, executiveSummary };
  };

  const { sections, demographicPositioning, executiveSummary } = getProcessedSections();

  return (
    <Box>
      {/* Regenerate Button */}
      {onRegenerate && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={onRegenerate}
            startIcon={<RefreshIcon />}
            size="small"
          >
            Regenerate
          </Button>
        </Box>
      )}
      
      {/* Mode Indicator - Admin only */}
      {showAdminControls && mode && (
        <Alert 
          severity={mode === 'openai' ? 'success' : (mode === 'stored' ? 'info' : 'warning')} 
          sx={{ mb: 2 }}
        >
          <strong>
            {mode === 'openai' 
              ? '✅ OpenAI API Active - Real AI Analysis' 
              : (mode === 'stored'
                  ? '📁 Loaded from Database - Fresh Data + Stored AI Narrative'
                  : '⚠️ MOCK MODE - OpenAI API Not Configured')}
          </strong>
          {mode === 'mock' && (
            <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.9em' }}>
              Using placeholder narrative for testing. Configure OpenAI API key to enable real AI analysis.
            </Box>
          )}
        </Alert>
      )}

      {/* Metadata - Admin only */}
      {showAdminControls && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {from_cache && (
            <Chip label="From Cache" color="info" size="small" />
          )}
          {mode === 'stored' && (
            <Chip label="Stored AI Narrative + Fresh Data" color="success" size="small" />
          )}
          {mode === 'openai' && (
            <Chip label="Live AI Generation" color="primary" size="small" />
          )}
          {tokens_used > 0 && (
            <Chip label={`${tokens_used} tokens used`} size="small" variant="outlined" />
          )}
          {sources && sources.length > 0 && (
            <Chip label={`${sources.length} news sources`} size="small" variant="outlined" color="secondary" />
          )}
        </Box>
      )}

      {/* Data Sources */}
      {showDataSources && summary.data_sources && (
        <DataSourcesInfo sources={[
          { label: "Market Share", value: summary.data_sources.market_share },
          { label: "Demographics", value: summary.data_sources.demographics },
          { label: "Focus Schools", value: summary.data_sources.focus_schools }
        ]} />
      )}

      {/* Executive Summary */}
      {executiveSummary && (
        <SectionRenderer 
          section={{ title: 'Executive Summary', content: executiveSummary }}
          showExplorerLink={!showAdminControls}
          selectedSchool={selectedSchool}
          summaryType="competitive"
        />
      )}

      {/* AI Narrative Sections (except Demographic Positioning and Market Share Analysis) */}
      {sections.map((section, index) => (
        <SectionRenderer 
          key={index} 
          section={section}
          showExplorerLink={!showAdminControls}
          selectedSchool={selectedSchool}
          summaryType="competitive"
        />
      ))}

      {/* Demographic Positioning and Demographics Chart Side by Side */}
      {demographicPositioning && (
        <Grid container spacing={3} sx={{ mb: 2, alignItems: 'stretch' }}>
          <Grid item xs={12} md={data?.demographics ? 6 : 12} sx={{ display: 'flex' }}>
            <SectionRenderer 
              section={demographicPositioning} 
              sx={{ flex: 1, height: '100%', mb: 0 }}
              showExplorerLink={!showAdminControls}
              selectedSchool={selectedSchool}
              summaryType="competitive"
            />
          </Grid>
          {data?.demographics && (
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <DemographicsChart demographicsData={data.demographics} />
            </Grid>
          )}
        </Grid>
      )}

      {/* Focus Schools and New Threats Side by Side */}
      {(data?.focus_schools?.length > 0 || data?.new_expanding_schools?.length > 0) && (
        <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
          {/* Focus Schools Recommendation */}
          {data?.focus_schools && data.focus_schools.length > 0 && (
            <Grid item xs={12} md={data?.new_expanding_schools?.length > 0 ? 7 : 12} sx={{ display: 'flex' }}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3,
                  backgroundColor: '#ffffff', 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    backgroundColor: '#673ab7'
                  }
                }}
              >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5, ml: 0.5 }}>
                    <Box sx={{ color: '#673ab7', display: 'flex', alignItems: 'center', '& svg': { fontSize: 28 } }}>
                      <TrendingUpIcon />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#673ab7' }}>
                      Recommended Focus Schools
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 0.5 }}>
                    Positive market share growth - key competitive threats:
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>School</strong></TableCell>
                          <TableCell><strong>Type</strong></TableCell>
                          <TableCell align="right"><strong>Enroll</strong></TableCell>
                          <TableCell align="right"><strong>Share</strong></TableCell>
                          <TableCell align="right"><strong>Δ</strong></TableCell>
                          <TableCell align="right"><strong>Trend</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.focus_schools.slice(0, 5).map((school, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {school.name}
                            </TableCell>
                            <TableCell>{school.governance}</TableCell>
                            <TableCell align="right">{school.enrollment.toLocaleString()}</TableCell>
                            <TableCell align="right">{typeof school.market_share_current === 'number' ? school.market_share_current.toFixed(1) : school.market_share_current}%</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${school.market_share_change >= 0 ? '+' : ''}${typeof school.market_share_change === 'number' ? school.market_share_change.toFixed(1) : school.market_share_change}%`}
                                color={school.market_share_change >= 0 ? 'success' : 'error'}
                                size="small"
                                sx={{ minWidth: 55 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography 
                                variant="body2" 
                                color={school.enrollment_trend > 0 ? 'success.main' : 'error.main'}
                              >
                                {school.enrollment_trend > 0 ? '+' : ''}{school.enrollment_trend}%
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
              </Paper>
            </Grid>
          )}

          {/* New/Expanding Schools */}
          {data?.new_expanding_schools && data.new_expanding_schools.length > 0 && (
            <Grid item xs={12} md={data?.focus_schools?.length > 0 ? 5 : 12} sx={{ display: 'flex' }}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3, 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column',
                  backgroundColor: '#ffffff',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    backgroundColor: '#673ab7'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5, ml: 0.5 }}>
                  <Box sx={{ color: '#673ab7', display: 'flex', alignItems: 'center', '& svg': { fontSize: 28 } }}>
                    <TrendingUpIcon />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#673ab7' }}>
                    New Competitive Threats
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 0.5 }}>
                  Recently opened or rapidly expanding schools:
                </Typography>
                <TableContainer sx={{ flex: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>School</strong></TableCell>
                        <TableCell align="right"><strong>Enroll</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.new_expanding_schools.slice(0, 5).map((school, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {school.name}
                          </TableCell>
                          <TableCell align="right">{school.enrollment_current.toLocaleString()}</TableCell>
                          <TableCell>
                            <Chip
                              label={school.status === 'new' ? 'New' : 'Expanding'}
                              color={school.status === 'new' ? 'success' : 'primary'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      <Grid container spacing={3}>

        {/* Recent News */}
        {sources && sources.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <OpenInNewIcon />
                Recent News & Developments
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                News articles about priority competitor schools:
              </Typography>
              <Grid container spacing={2}>
                {sources.map((source, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        {source.school && (
                          <Chip 
                            label={source.school} 
                            size="small" 
                            color="primary" 
                            sx={{ mb: 1 }} 
                          />
                        )}
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                          {source.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
                          {source.snippet}
                        </Typography>
                        <Link 
                          href={source.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            mt: 'auto'
                          }}
                        >
                          Read full article <OpenInNewIcon sx={{ ml: 0.5, fontSize: 16 }} />
                        </Link>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Debug Panel - Admin only */}
        {showAdminControls && (
          <Grid item xs={12}>
            <DebugPanel debug={summary.debug} data={summary.data} mode={mode} />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CompetitiveSummaryTab;

